import { NotFoundClient } from "@components/widgets/ErrorPage";
import {
  BrandCreditAccountsWhereColumn,
  SqlOperator,
  useCreditAccountsTableSummaryLazyQuery,
  useGetClientLazyQuery,
  useGetCreditAccountsLazyQuery,
} from "@src/__generated__/graphql";
import { PageLoading } from "@src/components/modules/clients/page-loading";
import { CardListCreditAccounts } from "@src/components/modules/credit-account/listing/CardListCreditAccounts";
import { BrandCreditAccountsSummaryModel } from "@src/components/modules/credit-account/listing/models";
import { PageCreditAccountsCtx } from "@src/components/modules/credit-account/listing/PageCreditAccounts";
import { StoreCreditAccounts } from "@src/components/modules/credit-account/listing/StoreCreditAccounts";
import { NavigationClient } from "@src/components/widgets/AppSubNav";
import { getQueryParam } from "@src/utils/routing";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

function useData(store: StoreCreditAccounts, clientId: string) {
  const [fetchClient, client] = useGetClientLazyQuery({
    variables: { id: clientId },
    onCompleted(data) {
      if (data.client) {
        store.where.implicitFilters.push({
          column: BrandCreditAccountsWhereColumn.BrandId,
          value: data.client.brands.map((brand) => brand.id),
          operator: SqlOperator.In,
        });
      }
    },
  });

  const [fetchCreditAccountsSummary, { loading: loadingSummary }] =
    useCreditAccountsTableSummaryLazyQuery({
      onCompleted(data) {
        if (!data.brandCreditAccountsSummary) return;
        store.brandCreditAccountsSummary = new BrandCreditAccountsSummaryModel(
          data.brandCreditAccountsSummary,
        );
      },
    });
  const [fetchCreditAccounts, { loading }] = useGetCreditAccountsLazyQuery({
    onCompleted(data) {
      store.fromGetCreditAccountsQuery(data);
    },
  });

  useEffect(() => {
    fetchClient();
    fetchCreditAccounts({ variables: store.queryParams });
    fetchCreditAccountsSummary({ variables: store.queryParams });
  }, []);

  return { client: client.data?.client, loading, loadingSummary };
}

export const PageClientCreditAccounts = observer(
  function PageClientCreditAccounts() {
    const { query } = useRouter();
    const clientId = getQueryParam("id", query);

    const [store] = useState(() => {
      const s = new StoreCreditAccounts(true);

      s.where.filters = s.where.filters.filter(
        (f) => ![BrandCreditAccountsWhereColumn.BrandId].includes(f.column),
      );

      return s;
    });

    const { client, loading, loadingSummary } = useData(store, clientId);

    if (loading) {
      return <PageLoading clientId={clientId} />;
    }

    if (!client) {
      return <NotFoundClient />;
    }

    return (
      <PageCreditAccountsCtx.Provider value={store}>
        <div className="flex justify-center items-center">
          <div className="w-full">
            <CardListCreditAccounts
              clientId={parseInt(clientId, 10)}
              isSummaryLoaded={!loadingSummary}
              navigation={<NavigationClient id={clientId} variant="page" />}
            />
          </div>
        </div>
      </PageCreditAccountsCtx.Provider>
    );
  },
);
